import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';

import { PageHeader, Form, Input, Button, Cascader, Spin, message, InputNumber, DatePicker, Select } from 'antd';
import ColorHash from 'color-hash';

import * as ToolRequest from '../../tools/ToolRequest';

import SpinFull from '../../components/SpinFull';
import CropNameBadge from '../../components/CropNameBadge';

const colorHash = new ColorHash();

const ZoneInput = ({ value, onChange, style, ...props }) => (
  <Input
    {...props}
    value={value}
    onChange={onChange}
    style={{
      ...style,
      ...(value ? { backgroundColor: colorHash.hex(value) } : null),
    }}
  />
);

const formLayout = {
  labelCol: {
    sm: { span: 5 },
  },
  wrapperCol: {
    sm: { span: 16 },
  },
  labelWrap: true,
};

const formTailLayout = {
  wrapperCol: {
    sm: { offset: 5, span: 16 },
  },
};

const typeLabels = {
  SOW: 'Sowing',
  TRANSPLANT: 'Transplanting',
  HARVEST: 'Harvesting',
  WASTE: 'Wastage',
};

const sourceLabels = {
  LOCAL: 'Local',
  EXTERNAL: 'External',
};

const stageLabels = {
  PROPAGATION: 'Propagation',
  PRODUCTION: 'Production',
};

const { Option } = Select;

export default function FarmProductionRecordEditScreen(props) {
  const history = useHistory();
  const { recordId } = useParams();

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState(null);
  const [recordType, setRecordType] = useState(null);
  const [site, setSite] = useState(null);
  const [crop, setCrop] = useState(null);

  useEffect(() => {
    (async () => {
      const recordRes = await ToolRequest.request('GET', `/v1/farm-production-record/${recordId}`);
      setRecordType(recordRes.type);
      setRecord({
        ...recordRes,
        siteId: [recordRes.siteId],
        cropId: [recordRes.cropId],
        type: [recordRes.type],
        source: recordRes.source || 'LOCAL',
        stage: recordRes.stage || 'PROPAGATION',
        recordedAt: moment(recordRes.recordedAt),
      });

      const siteRes = await ToolRequest.request('GET', `/v1/farm-site/${recordRes.siteId}`);
      setSite(siteRes);

      const cropRes = await ToolRequest.request('GET', `/v1/farm-crop/${recordRes.cropId}`);
      setCrop(cropRes);

      setLoaded(true);
    })();
  }, []);

  const onFinish = async (values) => {
    setLoading(true);

    try {
      const data = {
        amount: values.amount,
        zone: values.zone,
        recordedAt: moment(values.recordedAt).toISOString(),
        remarks: values.remarks,
      };

      if (recordType === 'HARVEST' && values.weight !== undefined) {
        data.weight = values.weight;
      }

      if ((recordType === 'SOW' || recordType === 'TRANSPLANT') && values.source) {
        data.source = values.source;
      }

      if (recordType === 'WASTE' && values.stage) {
        data.stage = values.stage;
      }

      await ToolRequest.request('PUT', `/v1/farm-production-record/${recordId}`, data);

      message.success('Update Production Record successfully');

      history.push('/farm-production-record');
    } catch (err) {
      setLoading(false);

      if (err?.response?.data) {
        switch (err.response.data) {
          default:
            return message.error('Unknown errors. Please retry');
        }
      }

      message.error('Unknown error. Please retry');
    }
  };

  return loaded ? (
    <PageHeader
      title={`Edit Production Record (${recordId})`}
      onBack={() => history.push('/farm-production-record')}
    >
      <Spin spinning={loading}>
        <Form
          {...formLayout}
          initialValues={record}
          onFinish={onFinish}
        >
          <Form.Item
            label="Site"
            name="siteId"
          >
            <Cascader
              options={[
                {
                  value: site.id,
                  label: site.name,
                },
              ]}
              disabled={true}
            />
          </Form.Item>

          <Form.Item
            label="Crop"
            name="cropId"
          >
            <Cascader
              options={[
                {
                  value: crop.id,
                  label: <CropNameBadge name={crop.name} />,
                },
              ]}
              disabled={true}
            />
          </Form.Item>

          <Form.Item
            label="Type"
            name="type"
          >
            <Cascader
              options={[
                {
                  value: recordType,
                  label: typeLabels[recordType] || recordType,
                },
              ]}
              disabled={true}
            />
          </Form.Item>

          {(recordType === 'SOW' || recordType === 'TRANSPLANT') && (
            <Form.Item
              label="Source"
              name="source"
              rules={[
                {
                  required: true,
                  message: 'Please select the source',
                },
              ]}
            >
              <Select placeholder="Select source">
                {Object.keys(sourceLabels).map(key => (
                  <Option key={key} value={key}>
                    {sourceLabels[key]}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {recordType === 'WASTE' && (
            <Form.Item
              label="Stage"
              name="stage"
              rules={[
                {
                  required: true,
                  message: 'Please select the stage',
                },
              ]}
            >
              <Select placeholder="Select stage">
                {Object.keys(stageLabels).map(key => (
                  <Option key={key} value={key}>
                    {stageLabels[key]}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Amount"
            name="amount"
            rules={[
              {
                required: true,
                message: 'Please enter the amount',
              },
            ]}
          >
            <InputNumber
              placeholder="1"
              min={1}
            />
          </Form.Item>

          {recordType === 'HARVEST' && (
            <Form.Item
              label="Weight (g)"
              name="weight"
            >
              <InputNumber
                placeholder="Weight"
                min={1}
              />
            </Form.Item>
          )}

          <Form.Item
            label="Zone"
            name="zone"
          >
            <ZoneInput
              placeholder="Zone"
            />
          </Form.Item>

          <Form.Item
            label="Recorded At"
            name="recordedAt"
            rules={[
              {
                required: true,
                message: 'Please select the recorded date',
              },
            ]}
          >
            <DatePicker showTime />
          </Form.Item>

          <Form.Item
            label="Remarks"
            name="remarks"
          >
            <Input.TextArea
              placeholder="Remarks"
            />
          </Form.Item>

          <Form.Item {...formTailLayout}>
            <Button type="primary" htmlType="submit" block>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Spin>
    </PageHeader>
  ) : <SpinFull />;
};
